import React, { useEffect, useState } from "react";
import HeartLoader from "@/components/HeartLoader";
import { useParams, Link } from "react-router-dom";
import { Loader2, MapPin, Sparkles, Flame, UsersRound, ArrowLeft, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { ConnectButton } from "@/components/ConnectButton";
import { toast } from "sonner";

export default function PublicProfile() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/profile/${userId}`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="min-h-screen grid place-items-center"><HeartLoader /></div>;
  if (!data) return <div className="min-h-screen grid place-items-center px-6 text-center"><div><h1 className="font-serif text-2xl">Profile not found</h1><Link to="/" className="text-brand hover:underline text-sm">Go home</Link></div></div>;

  const p = data.profile; const s = data.stats || {};
  const initial = (p.name || "?")[0]?.toUpperCase();

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-24 pb-16" data-testid="public-profile-page">
      <Link to="/personal?tab=community" className="text-xs text-sec hover:text-brand flex items-center gap-1 mb-4"><ArrowLeft size={13} /> Back to community</Link>
      <div className="rud-card rounded-2xl p-6">
        <div className="flex items-start gap-4">
          {p.avatar_url
            ? <img src={p.avatar_url} alt="" className="w-20 h-20 rounded-full object-cover" />
            : <div className="w-20 h-20 rounded-full bg-brand/15 text-brand grid place-items-center text-2xl font-bold">{initial}</div>}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-serif text-2xl" data-testid="pp-name">{p.name}</h1>
              {s.kind_soul && <span data-testid="pp-kind-soul" className="inline-flex items-center gap-1 text-[11px] text-brand bg-brand/15 rounded-full px-2 py-0.5"><Sparkles size={11} /> Kind Soul</span>}
            </div>
            {p.headline && <p className="text-sm text-sec mt-0.5">{p.headline}</p>}
            {p.location && <p className="text-xs text-sec mt-1 flex items-center gap-1"><MapPin size={11} /> {p.location}</p>}
          </div>
          {!data.is_mine && <ConnectButton userId={userId} />}
          <button data-testid="share-profile-btn" onClick={async () => {
            const url = `${window.location.origin}/u/${userId}`;
            try {
              if (navigator.share) { await navigator.share({ title: `${p.name} on My Kind`, text: `Meet ${p.name} on My Kind — one Earth, one family 🌍`, url }); }
              else { await navigator.clipboard.writeText(url); toast.success("Profile link copied — share it with the world 🌍"); }
            } catch { /* user cancelled */ }
          }} className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5 text-brand"><Share2 size={13} /> Share</button>
        </div>

        {p.bio && <p className="text-sm text-sec mt-4 leading-relaxed">{p.bio}</p>}

        <div className="grid grid-cols-4 gap-2 mt-5 text-center">
          <div className="rounded-xl border border-rud bg-white/5 p-2.5"><div className="text-lg font-serif text-brand">{s.kindness ?? 100}%</div><div className="text-[10px] text-sec">Kindness</div></div>
          <div className="rounded-xl border border-rud bg-white/5 p-2.5"><div className="text-lg font-serif flex items-center justify-center gap-0.5"><Flame size={13} className="text-orange-400" />{s.streak || 0}</div><div className="text-[10px] text-sec">Streak</div></div>
          <div className="rounded-xl border border-rud bg-white/5 p-2.5"><div className="text-lg font-serif flex items-center justify-center gap-0.5"><UsersRound size={13} className="text-brand" />{s.circle || 0}</div><div className="text-[10px] text-sec">Circle</div></div>
          <div className="rounded-xl border border-rud bg-white/5 p-2.5"><div className="text-lg font-serif">{s.followers || 0}</div><div className="text-[10px] text-sec">Followers</div></div>
        </div>

        {p.skills?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {p.skills.map((sk, i) => <span key={i} className="text-[11px] rounded-full bg-white/5 border border-rud px-2.5 py-1 text-sec">{sk}</span>)}
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="text-sm font-medium mb-2">Posts ({data.posts?.length || 0})</div>
        {(!data.posts || data.posts.length === 0) ? (
          <p className="text-sm text-sec">No posts yet.</p>
        ) : (
          <div className="space-y-2" data-testid="pp-posts">
            {data.posts.map((post) => (
              <div key={post.id} className="rud-card rounded-xl p-4">
                {post.title && <div className="text-sm font-medium">{post.title}</div>}
                <div className="text-sm text-sec mt-1 whitespace-pre-line">{post.body}</div>
                {post.media_url && post.media_type === "image" && <img src={post.media_url} alt="" className="mt-2 rounded-lg max-h-64 object-cover w-full" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
